<template>
  <v-hover v-slot="{ hover }">
    <div>
      <v-dialog v-model="applicationConfigurationEditDialog" scrollable max-width="1250px">
        <template v-slot:activator="{ on, attrs }">
          <v-card class="pa-3 mb-4 application-container" :elevation="hover ? 8 : 2" :class="{ 'on-hover': hover }">
            <v-expansion-panels accordion flat>
              <v-expansion-panel class="application-expansion-container" :key="`application-${application.name}`">
                <v-expansion-panel-header class="expandable-title" expand-icon="">
                  <div class="title-container">
                    <div class="description">
                      <v-card-title class="title">
                        {{ application.name }}
                      </v-card-title>
                      <v-card-subtitle class="subtitle">
                        Port: {{ application.port }}
                      </v-card-subtitle>
                    </div>
                    <div>
                      {{ application.releaseVersions[0] }}
                    </div>
                    <v-btn class="edit-btn" icon color="#42b983" v-bind="attrs" v-on="on">
                      <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="application-expansion-content-container">
                  <template v-for="(
                      envType, currentEnvTypeIndex
                    ) in availableEnvironments">
                    <v-expansion-panels class="environment-configuration-information-container"
                      :key="`envType-${currentEnvTypeIndex}`" accordion flat>
                      <v-expansion-panel
                        v-for="(environmentConfiguration, i) in filteredEnvironmentConfigurations(envType.name)"
                        :key="`key-${i}`">
                        <div class="environment-configuration-divider" v-if="currentEnvTypeIndex > 0"></div>

                        <v-expansion-panel-header class="expandable-title">
                          <div>
                            {{ environmentConfiguration.environmentType.displayName }}
                            <span class="subtitle"><br /> {{ generateSubtitle(application, environmentConfiguration,
                              deploymentCache) }}</span>
                          </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content class="text-left expandable-content">
                          <div class="expandable-content-container">
                            <p>
                              Host:
                              <span class="float-right">{{ environmentConfiguration.host.displayName }}</span>
                            </p>
                            <p>
                              Type:
                              <span class="float-right">{{ environmentConfiguration.applicationType.displayName }}</span>
                            </p>

                            <template
                              v-if="deploymentInformation = evaluateDeploymentInformation(application.name, envType.name, deploymentCache, environmentConfiguration.instanceIdentifier)">
                              <p v-if="deploymentInformation.version != null">
                                Version:
                                <span class="float-right">{{ deploymentInformation.version }}</span>
                              </p>
                              <p class="deployment-timestamp" title="Time this instance registered with Eureka">
                                <span>Registered at:</span>
                                <span>{{ formatDeploymentDate(deploymentInformation.registeredAt) }}</span>
                              </p>
                              <p class="deployment-timestamp" title="Recorded when the deployment notification arrived">
                                <span>Deployed at:</span>
                                <span>{{ formatDeploymentDate(deploymentInformation.deployedAt, 'Not recorded') }}</span>
                              </p>
                              <p v-if="deploymentInformation.branch != null">
                                Branch:
                                <span class="float-right">{{ deploymentInformation.branch }}</span>
                              </p>
                              <p v-if="deploymentInformation.commit != null">
                                Commit:
                                <span class="float-right">{{ deploymentInformation.commit.substring(0, 10) }}</span>
                              </p>
                            </template>
                          </div>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                    <div class="environment-configuration-group-divider" :key="`divider-${currentEnvTypeIndex}`"></div>
                  </template>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
            <v-card-actions :elevation="hover ? 8 : 2"  :class="{ 'on-hover': hover }" class="action-container">
              <div class="action action-bitbucket">
                <v-btn icon target="_blank" :href="`https://bitbucket.org/dericon/${application.name}`">
                  <v-icon>mdi-bitbucket</v-icon>
                </v-btn>
              </div>
            </v-card-actions>
          </v-card>
        </template>
        <v-card class="dialog-container">
          <div class="title-container">
            <v-card-title class="title">
              <h1>{{ applicationEditModel.name }}</h1>
            </v-card-title>
          </div>
          <v-card-text class="configuration-container">
            <v-container>
              <v-row>
                <v-col cols="4">
                  <v-row>
                    <v-col cols="12">
                      <v-text-field label="Application Name" v-model="applicationEditModel.name" required></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-subheader class="pa-0">Port</v-subheader>
                      <v-row>
                        <v-slider v-model="applicationEditModel.port" class="applicationPortSlider mx-0 px-0" :min="0"
                          :max="portRange.max" hide-details :thumb-label="true" thumb-size="48" track-color="secondary">
                          <template v-slot:append>
                            <v-text-field v-model="applicationEditModel.port" class="ma-0 pa-0" hide-details single-line
                              type="number" style="width: 60px"></v-text-field>
                          </template>
                        </v-slider>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-col>
                <v-spacer></v-spacer>
                <v-col cols="7">
                  <div class="environment-configuration-container" v-for="(
                      envConfig, index
                    ) in applicationEditModel.environmentConfigurations" :key="`envConfig-${index}`">
                    <v-col cols="10">
                      <div class="header">
                        <h3>Config {{ index }}:</h3>
                        <v-btn class="edit-btn" icon color="#DD0124" v-on:click="removeEnvConfig(index)">
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </div>
                      <v-subheader class="pa-0">Host</v-subheader>
                      <div>
                        <v-select class="select-input" color="#42b983" :items="availableHosts" item-text="displayName"
                          item-value="name" v-model="envConfig.host.name">
                        </v-select>
                      </div>
                      <v-subheader class="pa-0">Application Type</v-subheader>
                      <div>
                        <v-select class="select-input" color="#42b983" :items="availableApplicationTypes"
                          item-text="displayName" item-value="name" v-model="envConfig.applicationType.name">
                        </v-select>
                      </div>
                      <v-subheader class="pa-0">Environment Type</v-subheader>
                      <div>
                        <v-select class="select-input" color="#42b983" :items="availableEnvironments"
                          item-text="displayName" item-value="name" v-model="envConfig.environmentType.name">
                        </v-select>
                      </div>
                      <template v-if="envConfig.instanceIdentifier != null">
                        <v-text-field label="Instance Identifier (Label)"
                          v-model="envConfig.instanceIdentifier.displayName"></v-text-field>
                      </template>
                    </v-col>
                  </div>
                  <v-row class="pa-5">
                    <v-spacer></v-spacer>
                    <v-btn @click="
                      environmentConfigurationEditDialog =
                      !environmentConfigurationEditDialog
                      " class="send-btn" color="#42b983" dark text large>
                      Add Environment Configuration
                      <v-icon right>mdi-plus</v-icon>
                    </v-btn>
                  </v-row>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="#42b983" text @click="applicationConfigurationEditDialog = false">Cancel</v-btn>
            <v-btn color="#42b983" text @click="save">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="environmentConfigurationEditDialog" max-width="500px">
        <v-card class="dialog-container">
          <div class="title-container">
            <v-card-title class="title">
              <h2>New Environment Config</h2>
            </v-card-title>
          </div>
          <v-card-text class="configuration-container">
            <div class="environment-configuration-container">
              <v-col cols="10">
                <v-subheader class="pa-0">Host</v-subheader>
                <div>
                  <v-select class="select-input" color="#42b983" :items="availableHosts" item-text="displayName"
                    item-value="name" v-model="newEnvironmentConfig.host.name">
                  </v-select>
                </div>
                <v-subheader class="pa-0">Application Type</v-subheader>
                <div>
                  <v-select class="select-input" color="#42b983" :items="availableApplicationTypes"
                    item-text="displayName" item-value="name" v-model="newEnvironmentConfig.applicationType.name">
                  </v-select>
                </div>
                <v-subheader class="pa-0">Environment Type</v-subheader>
                <div>
                  <v-select class="select-input" color="#42b983" :items="availableEnvironments" item-text="displayName"
                    item-value="name" v-model="newEnvironmentConfig.environmentType.name">
                  </v-select>
                </div>
                <v-text-field label="Instance Identifier (Label)"
                  v-model="newEnvironmentConfig.instanceIdentifier.displayName"></v-text-field>
                <v-text-field label="Instance Identifier"
                  v-model="newEnvironmentConfig.instanceIdentifier.name"></v-text-field>
              </v-col>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="#42b983" text @click="environmentConfigurationEditDialog = false">Cancel</v-btn>
            <v-btn color="#42b983" text @click="addEnvironmentConfiguration">Add Configuration</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </v-hover>
</template>

<script>
import ApplicationService from "../../services/ApplicationService";

export default {
  name: "EditApplication",
  data: () => {
    return {
      applicationConfigurationEditDialog: false,
      environmentConfigurationEditDialog: false,
      applicationEditModel: {},
      newEnvironmentConfig: {
        environmentType: {},
        applicationType: {},
        host: {},
        instanceIdentifier: {
          displayName: null,
          name: null,
        },
      },
    };
  },
  watch: {
    applicationConfigurationEditDialog: function (val) {
      if (val === true) {
        this.applicationEditModel = JSON.parse(
          JSON.stringify(this.application)
        );
      }
    },
    environmentConfigurationEditDialog: function (val) {
      if (val === true) {
        this.newEnvironmentConfig = {
          environmentType: {},
          applicationType: {},
          host: {},
          instanceIdentifier: {
            displayName: null,
            name: null,
          },
        };
      }
    },
  },
  props: [
    "application",
    "deploymentCache",
    "portRange",
    "availableHosts",
    "availableEnvironments",
    "availableApplicationTypes",
  ],
  methods: {
    evaluateDeploymentInformation(applicationName, environmentType, deploymentCache, instanceIdentifier) {
      const applicationId = instanceIdentifier && instanceIdentifier.name
        ? `${applicationName}-${instanceIdentifier.name}`
        : applicationName;
      if (deploymentCache && deploymentCache[applicationId] && deploymentCache[applicationId][environmentType]) {
        return deploymentCache[applicationId][environmentType];
      }
      // If deploymentCache, name or type are not defined, return null.
      return null;
    },
    formatDeploymentDate(value, fallback = 'Not available') {
      if (value == null) {
        return fallback;
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return fallback;
      }
      return new Intl.DateTimeFormat('en-GB', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).format(date);
    },
    save: function () {
      let updatedConfiguration = {
        id: this.applicationEditModel.id,
        name: this.applicationEditModel.name,
        port: this.applicationEditModel.port,
        environmentConfigurations:
          this.applicationEditModel.environmentConfigurations.map(function (
            envConfig
          ) {
            return {
              environmentType: envConfig.environmentType.name,
              applicationType: envConfig.applicationType.name,
              host: envConfig.host.name,
              instanceIdentifier: envConfig.instanceIdentifier,
            };
          }),
      };
      console.log(updatedConfiguration);
      ApplicationService.saveApplicationConfiguration(
        updatedConfiguration
      ).then((updatedApplication) => {
        console.log("Application Edited");
        this.applicationConfigurationEditDialog = false;
        this.$emit("applicationEdited");
      });
    },
    generateSubtitle(application, environmentConfig, deploymentCache) {

      let evaluate = function () {
        let applicationName = environmentConfig.instanceIdentifier && environmentConfig.instanceIdentifier.name
          ? `${application.name}-${environmentConfig.instanceIdentifier.name}`
          : application.name
        let environmentType = environmentConfig.environmentType.name

        if (applicationName && environmentType && deploymentCache) {

          // Check if deploymentCache, name and type are defined.
          if (deploymentCache && deploymentCache != null) {
            let applicationConfig = deploymentCache[applicationName];
            if (applicationConfig && applicationConfig != null) {
              let envConfig = applicationConfig[environmentType];
              if (envConfig && envConfig != null) {
                let branch = envConfig.branch
                let commit = envConfig.commit
                let version = envConfig.version

                if (branch != null || commit != null || version != null) {
                  return envConfig
                }
              }
            }
          }
        }

        return null;
      }


      let deploymentInformation = evaluate()

      if (deploymentInformation != null) {
        let latestVersion = application.releaseVersions[0];
        let instanceIdentifier = environmentConfig.instanceIdentifier;
        if (instanceIdentifier != null) {
          instanceIdentifier = instanceIdentifier.displayName;
        }

        if (environmentConfig.environmentType.name === "TEST_ENVIRONMENT") {
          let resolveVersion = function () {
            if (deploymentInformation.commit != null) {
              return (
                deploymentInformation.branch + " (" + deploymentInformation.commit.substring(0, 10) + ")"
              );
            }
            return null;
          };
          let version = resolveVersion();
          let resolveSubtitle = function () {
            if (instanceIdentifier != null && version != null) {
              return instanceIdentifier + " - " + version;
            } else if (instanceIdentifier != null) {
              return instanceIdentifier;
            } else if (version != null) {
              return version;
            } else {
              return "";
            }
          };

          return resolveSubtitle();
        } else if (
          environmentConfig.environmentType.name === "PRODUCTION_ENVIRONMENT"
        ) {
          let resolveVersion = function () {
            let subtitleVersion = deploymentInformation.version
              .replaceAll("-RELEASE", "")
              .replaceAll("-SNAPSHOT", "");
            if (latestVersion != null && subtitleVersion !== latestVersion) {
              return subtitleVersion + " --> " + latestVersion;
            } else {
              return subtitleVersion;
            }
          };
          let version = resolveVersion();
          let resolveSubtitle = function () {
            if (instanceIdentifier != null && version != null) {
              return instanceIdentifier + " - " + version;
            } else if (instanceIdentifier != null) {
              return instanceIdentifier;
            } else if (version != null) {
              return version;
            } else {
              return "";
            }
          };

          return resolveSubtitle();
        }
      }

      return "";
    },
    addEnvironmentConfiguration() {
      let addedConfig = Object.assign({}, this.newEnvironmentConfig);
      console.log(addedConfig);
      if (addedConfig.instanceIdentifier.name == null) {
        addedConfig.instanceIdentifier = null;
      }
      this.applicationEditModel.environmentConfigurations.push(addedConfig);
      this.environmentConfigurationEditDialog = false;
    },
    filteredEnvironmentConfigurations: function (name) {
      let filteredConfigs = this.application.environmentConfigurations.filter(
        function (envConfig) {
          return envConfig.environmentType.name === name;
        }
      );

      return filteredConfigs;
    },
    removeEnvConfig: function (index) {
      this.applicationEditModel.environmentConfigurations.splice(index, 1);
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.application-container {
  text-align: left;
}

.deployment-timestamp {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 4px 8px;
}

.deployment-timestamp > span {
  white-space: nowrap;
}

.deployment-timestamp > span:last-child {
  margin-left: auto;
}

.application-container .title-container {
  display: flex;
  align-items: center;
}

.application-container .title-container .description {
  flex-grow: 1;
}

.application-container .edit-btn {
  padding: 32px;
}

.application-container .title {
  color: #42b983;
  font-weight: bold;
}

.subtitle {
  text-align: left;
}

.expandable-title {
  min-height: 64px;
}

.v-expansion-panel-header--active,
.expandable-title:hover {
  color: #42b983;
}

.v-expansion-panel-header .subtitle {
  line-height: 24px;
  font-size: 0.9em;
  color: rgba(0, 0, 0, 0.6);
}

.v-expansion-panel-header--active:before {
  color: inherit;
}

.expandable-content .expandable-content-container {
  padding: 0 0 0 18px;
}

.dialog-container .title-container .title {
  padding: 36px 48px 0;
}

.dialog-container h2 {
  text-align: left;
}

.dialog-container .configuration-container {
  text-align: left;
}

.dialog-container .environment-configuration-container {
  margin: 0 24px 24px;
}

.dialog-container .environment-configuration-container .v-subheader {
  height: auto;
  margin-top: 12px;
}

.dialog-container .environment-configuration-container h3 {
  color: #42b983;
  font-size: x-large;
}

.dialog-container .environment-configuration-container .select-input {
  padding: 0;
}

.dialog-container .environment-configuration-container .header {
  display: flex;
  align-items: center;
}

.dialog-container .environment-configuration-container .header h3 {
  flex-grow: 1;
}

.dialog-container .v-card__actions {
  padding: 24px;
}

.environment-configuration-divider {
  display: none;
}

.environment-configuration-group-divider+.environment-configuration-information-container>div:first-child .environment-configuration-divider {
  display: block;
  width: 100%;
  height: 1px;
  margin: 15px 0;
  background-color: #dcdee0;
}

.application-expansion-container>button {
  padding: 0;
}

.application-expansion-content-container>div {
  padding: 0;
}

.application-container .action-container {
  padding: 0 16px;
}

.application-container .action-container .action {

}

</style>
